import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useSupervisorAssessments } from '@/api/supervisor';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { cn } from '@/lib/utils';
import { Plus, User, Calendar, CheckCircle, Clock, AlertCircle, Star } from 'lucide-react';
import type { SupervisorAssessment } from '@/types';
import { CreateAssessmentDialog } from './_components/create-assessment-dialog';

type TabKey = 'pending' | 'in_progress' | 'completed';

const TAB_MAP: Record<TabKey, { label: string; status: string }> = {
  pending: { label: 'Pendientes', status: 'pending' },
  in_progress: { label: 'En Progreso', status: 'in_progress' },
  completed: { label: 'Completadas', status: 'completed' },
};

const STATUS_BADGES: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  pending: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  in_progress: { label: 'En Progreso', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  completed: { label: 'Completada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
};

const TYPE_LABELS: Record<string, string> = {
  probation: 'Período de prueba',
  periodic: 'Periódica',
  competency_check: 'Verificación de competencia',
};

export function SupervisorAssessmentsPage() {
  const [tab, setTab] = useState<TabKey>('pending');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const { data, isLoading, error } = useSupervisorAssessments({ status: TAB_MAP[tab].status });

  const assessments = data?.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Evaluaciones</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Crea y gestiona evaluaciones de competencia para tu equipo.
          </p>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Crear
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-muted p-1">
        {(Object.keys(TAB_MAP) as TabKey[]).map((key) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              tab === key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {TAB_MAP[key].label}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message="No se pudieron cargar las evaluaciones." />
      ) : assessments.length === 0 ? (
        <EmptyState
          title="Sin evaluaciones"
          description={`No hay evaluaciones ${TAB_MAP[tab].label.toLowerCase()}.`}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {assessments.map((a) => (
            <AssessmentCard key={a.id} assessment={a} />
          ))}
        </div>
      )}

      {/* Create dialog */}
      {showCreateDialog && <CreateAssessmentDialog onClose={() => setShowCreateDialog(false)} />}
    </div>
  );
}

function AssessmentCard({ assessment }: { assessment: SupervisorAssessment }) {
  const navigate = useNavigate();
  const badge = (STATUS_BADGES[assessment.status] ?? STATUS_BADGES.pending)!;
  const BadgeIcon = badge.icon;

  return (
    <div
      onClick={() => navigate(`/competency/assessments/${assessment.id}`)}
      className="cursor-pointer rounded-xl border border-border bg-card p-4 space-y-3 hover:shadow-md transition-shadow"
    >
      {/* Header: type + status */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase">
          {TYPE_LABELS[assessment.assessment_type] ?? assessment.assessment_type}
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
            badge.color,
          )}
        >
          <BadgeIcon className="h-3 w-3" />
          {badge.label}
        </span>
      </div>

      {/* Assessed user */}
      {assessment.assessed && (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-card-foreground">
            {assessment.assessed.first_name} {assessment.assessed.last_name}
          </span>
        </div>
      )}

      {/* Score if completed */}
      {assessment.status === 'completed' && assessment.score !== null && (
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 text-amber-500" />
          <span className="text-sm text-card-foreground">
            Puntuacion: {assessment.score}
            {assessment.passed !== null && (
              <span className={cn('ml-2', assessment.passed ? 'text-green-600' : 'text-red-600')}>
                ({assessment.passed ? 'Aprobado' : 'No aprobado'})
              </span>
            )}
          </span>
        </div>
      )}

      {/* Date */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Calendar className="h-3.5 w-3.5" />
        <span>{new Date(assessment.created_at).toLocaleDateString('es')}</span>
      </div>

      {/* Comments preview */}
      {assessment.overall_comments && (
        <p className="text-xs text-muted-foreground line-clamp-2">{assessment.overall_comments}</p>
      )}
    </div>
  );
}
